import cn from 'classnames';
import Image from '@packages/shared/core/common/components/common/Image';
import { Expert } from '@packages/shared/core/experts/entities';
import Button from '@packages/shared/core/common/components/common/Button';
import ExpertAbout from '@packages/shared/core/common/components/common/ExpertCard/ExpertAbout';
import ExpertStatusIndicator from '@packages/shared/core/common/components/common/ExpertStatusIndicator';
import { DataTestId } from '@packages/shared/core/common/constants/dataTestId';
import { SCHEMA_TYPES, SCHEMA_ITEM_PROPS } from '@packages/shared/schema/constants';
import { CURRENCIES } from '@packages/shared/core/common/constants';
import { getExpertJobTitle } from '@packages/shared/schema/utils';
import { withDefaultOrigin } from '@packages/shared/core/common/utils/url';
import ExpertCardReviews from './ExpertCardReviews';
import ExpertCardConsultations from './ExpertCardConsultations';
import ExpertCardExperience from './ExpertCardExperience';
import ExpertCardSkills from './ExpertCardSkills';
import styles from './ExpertCard.module.scss';

export type ExpertCardProps = {
  className?: string;
  onStartChat: () => void;
  expert: Expert;
  expertSkillsLimit?: number;
};

const ExpertCard = ({ className = '', onStartChat, expert, expertSkillsLimit = 3 }: ExpertCardProps) => {
  const rootClasses = cn({
    [styles.root]: true,
    [styles.rootBestMatch]: expert.isBestMatch,
    [className]: !!className,
  });

  const skillsClasses = cn({
    [styles.skills]: true,
    [styles.skillsBestMatch]: expert.isBestMatch,
  });

  const descriptionClasses = cn({
    [styles.description]: true,
    [styles.descriptionBestMatch]: expert.isBestMatch,
  });

  const buttonClasses = cn({
    [styles.button]: true,
    [styles.buttonBestMatch]: expert.isBestMatch,
  });

  return (
    <div className={rootClasses} itemScope itemType={SCHEMA_TYPES.PERSON}>
      <div>
        {expert.isBestMatch && <span className={styles.badge}>Best Match</span>}

        <div className={styles.header}>
          <div className={styles.imageWrapper}>
            <Image
              type="future"
              className={styles.expertImage}
              src={expert.image}
              alt={expert.name}
              width={80}
              height={80}
            />

            <link itemProp={SCHEMA_ITEM_PROPS.IMAGE} href={withDefaultOrigin(expert.image)} />

            <ExpertStatusIndicator status={expert.status} />
          </div>

          <div>
            <p className={styles.name} itemProp={SCHEMA_ITEM_PROPS.NAME}>
              {expert.name}
            </p>

            <meta itemProp={SCHEMA_ITEM_PROPS.JOB_TITLE} content={getExpertJobTitle(expert)} />

            <ExpertAbout
              maxDescriptionLength={88}
              descriptionProps={{
                text: expert.description,
                className: descriptionClasses,
              }}
            />
          </div>
        </div>

        <meta
          itemProp={SCHEMA_ITEM_PROPS.DESCRIPTION}
          content={`${expert.experience} years of experience, ${expert.total_orders} consultations done`}
        />

        <ul className={styles.infoList}>
          <li className={styles.infoListItem}>
            <ExpertCardReviews expert={expert} />
          </li>

          <li
            className={styles.infoListItem}
            itemProp={SCHEMA_ITEM_PROPS.ADDITIONAL_PROPERTY}
            itemScope
            itemType={SCHEMA_TYPES.PROPERTY_VALUE}
          >
            <meta itemProp={SCHEMA_ITEM_PROPS.NAME} content="Consultations" />

            <ExpertCardConsultations consultations={expert.total_orders} />
          </li>

          <li
            className={styles.infoListItem}
            itemProp={SCHEMA_ITEM_PROPS.ADDITIONAL_PROPERTY}
            itemScope
            itemType={SCHEMA_TYPES.PROPERTY_VALUE}
          >
            <meta itemProp={SCHEMA_ITEM_PROPS.NAME} content="Experience" />

            <ExpertCardExperience experience={expert.experience} />
          </li>
        </ul>

        <ExpertCardSkills
          className={skillsClasses}
          specializations={expert.specializations}
          skillsLimit={expertSkillsLimit}
        />
      </div>

      <div className={styles.action} itemProp={SCHEMA_ITEM_PROPS.OFFERS} itemScope itemType={SCHEMA_TYPES.OFFER}>
        <meta itemProp={SCHEMA_ITEM_PROPS.PRICE} content={expert.chatPrice.toString()} />

        <meta itemProp={SCHEMA_ITEM_PROPS.PRICE_CURRENCY} content={CURRENCIES.USD} />

        <meta itemProp={SCHEMA_ITEM_PROPS.DESCRIPTION} content={`Chat for $${expert.chatPrice}/min`} />

        <Button
          wrapperClassName={styles.buttonWrapper}
          className={buttonClasses}
          onClick={onStartChat}
          data-testid={DataTestId.START_CHAT_BUTTON}
        >
          Start Free Chat
        </Button>
      </div>

      <div itemProp={SCHEMA_ITEM_PROPS.WORK_LOCATION} itemScope itemType={SCHEMA_TYPES.PLACE}>
        <meta itemProp={SCHEMA_ITEM_PROPS.NAME} content="Online" />
      </div>
    </div>
  );
};

export default ExpertCard;
